import { useEffect, useState } from 'react';

import { Tooltip } from 'primereact/tooltip';

import { Account } from '@/rtk-query.gql';
import { determineBillingSplitType } from '@/utils';

import { BillingSplitType } from '../constants';

type PartnershipSummaryProps = {
  currentPartnershipItem: any;
  className?: string;
  title?: string;
  originalSplitType?: BillingSplitType | null;
  setOriginalSplitType?: (splitType: BillingSplitType | null) => void;
};

export const PartnershipSummary = ({
  currentPartnershipItem,
  className = '',
  title = 'Current Partnership',
  originalSplitType,
  setOriginalSplitType,
}: PartnershipSummaryProps) => {
  const [displayedAccounts, setDisplayedAccounts] = useState<Account[]>([]);
  useEffect(() => {
    const hasAnyAccount =
      currentPartnershipItem?.account1 ||
      currentPartnershipItem?.account1Id ||
      currentPartnershipItem?.account2 ||
      currentPartnershipItem?.account2Id ||
      currentPartnershipItem?.account3 ||
      currentPartnershipItem?.account3Id ||
      currentPartnershipItem?.account4 ||
      currentPartnershipItem?.account4Id ||
      currentPartnershipItem?.percent1 != null;
    if (!currentPartnershipItem || !hasAnyAccount) return;

    const seenIds = new Set<string>();
    const accounts: Account[] = [];

    [1, 2, 3, 4].forEach((position) => {
      const account = currentPartnershipItem[`account${position}`];
      const id =
        (account as { id?: string } | null)?.id ??
        currentPartnershipItem[`account${position}Id`];
      if (id && !seenIds.has(id)) {
        seenIds.add(id);
        if (account && typeof account === 'object' && 'name' in account) {
          accounts.push(account as Account);
        } else {
          accounts.push({ id, name: id } as Account);
        }
      }
    });

    const splitType = determineBillingSplitType(currentPartnershipItem);

    setOriginalSplitType?.(splitType);
    setDisplayedAccounts(accounts);
  }, [currentPartnershipItem, setOriginalSplitType]);

  const hasAccountAtPosition = (n: number) =>
    Boolean(
      currentPartnershipItem?.[`account${n}`] ||
        currentPartnershipItem?.[`account${n}Id`],
    );

  const getPercentAt = (n: number) =>
    (currentPartnershipItem?.[`percent${n}`] as number | null) ?? 0;

  const getTotalPercent = () =>
    [1, 2, 3, 4].reduce(
      (sum, n) => (hasAccountAtPosition(n) ? sum + getPercentAt(n) : sum),
      0,
    );

  const isInvalidDistribution = () => {
    if (displayedAccounts.length === 0) return false;
    return Math.abs(getTotalPercent() - 100) > 0.01;
  };

  const getOptionPositionsLine = () => {
    if (!currentPartnershipItem || !originalSplitType) return null;
    const parts: { position: number; name: string }[] = [];
    [1, 2, 3, 4].forEach((position) => {
      const account = currentPartnershipItem[`account${position}`];
      const id =
        (account as { id?: string } | null)?.id ??
        currentPartnershipItem[`account${position}Id`];
      if (!id) return;
      const name =
        (account && typeof account === 'object' && 'name' in account
          ? (account as { name?: string }).name
          : null) ??
        displayedAccounts.find((a) => a.id === id)?.name ??
        id;
      parts.push({ position, name });
    });
    if (parts.length === 0) return null;
    return (
      <>
        {parts.map(({ position, name }, index) => (
          <span key={position}>
            {index > 0 && '  '}
            <span className="text-primary font-semibold">
              Option {position}:
            </span>{' '}
            <span className="text-gray-600">{name}</span>
          </span>
        ))}
      </>
    );
  };

  const getPartnerDistribution = () => {
    if (!originalSplitType) return null;

    switch (originalSplitType) {
      case BillingSplitType.FIFTY_FIFTY:
        return 'Partner A gets 2 options, Partner B gets 2 options';
      case BillingSplitType.SEVENTY_FIVE_TWENTY_FIVE:
        return 'Partner A gets 3 options, Partner B gets 1 option';
      case BillingSplitType.FIFTY_TWENTY_FIVE_TWENTY_FIVE:
        return 'Partner A gets 2 options, Partner B gets 1 option, Partner C gets 1 option';
      case BillingSplitType.TWENTY_FIVE_EACH:
        return 'Partner A gets 1 option, Partner B gets 1 option, Partner C gets 1 option, Partner D gets 1 option';
      case BillingSplitType.THIRTY_THREE_EACH:
        return 'Partner A gets 1 option, Partner B gets 1 option, Partner C gets 1 option (or one partner 2 options, one partner 1 option for 2/3–1/3)';
      case BillingSplitType.ONE_HUNDRED:
        return 'Partner A gets 1 option';
      default:
        return null;
    }
  };

  const partnershipSummary = () => {
    if (displayedAccounts.length === 0) {
      return 'No partnership defined';
    }
    if (!originalSplitType) {
      const entries: { name: string; percent: number | null }[] = [];
      const seen = new Set<string>();
      [1, 2, 3, 4].forEach((n) => {
        const account = currentPartnershipItem?.[`account${n}`];
        const id =
          (account as { id?: string } | null)?.id ??
          currentPartnershipItem?.[`account${n}Id`];
        const percent = currentPartnershipItem?.[`percent${n}`] as
          | number
          | null
          | undefined;
        if (id && !seen.has(id)) {
          seen.add(id);
          const name =
            (account && typeof account === 'object' && 'name' in account
              ? (account as { name?: string }).name
              : null) ??
            displayedAccounts.find((a) => a.id === id)?.name ??
            id;
          entries.push({ name: name ?? id, percent: percent ?? null });
        }
      });
      if (entries.length === 0) return 'No partnership defined';
      return (
        <>
          {entries.map(({ name, percent }, i) => (
            <span key={i}>
              {i > 0 && <span className="text-gray-600">, </span>}
              <span className="text-primary font-semibold">{name}</span>
              {percent != null && (
                <span className="text-gray-600"> ({percent}%)</span>
              )}
            </span>
          ))}
        </>
      );
    }

    const uniqueAccounts = new Map<string, number>();
    [1, 2, 3, 4].forEach((position) => {
      const account = currentPartnershipItem?.[`account${position}`];
      const id =
        (account as { id?: string } | null)?.id ??
        currentPartnershipItem?.[`account${position}Id`];
      if (id) {
        uniqueAccounts.set(id, (uniqueAccounts.get(id) || 0) + 1);
      }
    });

    switch (originalSplitType) {
      case BillingSplitType.FIFTY_FIFTY: {
        const accounts = Array.from(uniqueAccounts.entries()).map(([id]) => {
          return displayedAccounts.find((a) => a.id === id)?.name;
        });
        return (
          <>
            <span className="text-primary font-semibold">{accounts[0]}</span>
            <span className="text-gray-600"> (50%) and </span>
            <span className="text-primary font-semibold">{accounts[1]}</span>
            <span className="text-gray-600"> (50%)</span>
          </>
        );
      }

      case BillingSplitType.SEVENTY_FIVE_TWENTY_FIVE: {
        const [majorityId, minorityId] = Array.from(uniqueAccounts.entries())
          .sort(([, countA], [, countB]) => countB - countA)
          .map(([id]) => id);

        const majorityName = displayedAccounts.find(
          (a) => a.id === majorityId,
        )?.name;
        const minorityName = displayedAccounts.find(
          (a) => a.id === minorityId,
        )?.name;
        return (
          <>
            <span className="text-primary font-semibold">{majorityName}</span>
            <span className="text-gray-600"> (75%) and </span>
            <span className="text-primary font-semibold">{minorityName}</span>
            <span className="text-gray-600"> (25%)</span>
          </>
        );
      }

      case BillingSplitType.THIRTY_THREE_EACH: {
        const entries = Array.from(uniqueAccounts.entries()).sort(
          ([, countA], [, countB]) => countB - countA,
        );
        if (
          entries.length === 2 &&
          entries[0][1] === 2 &&
          entries[1][1] === 1
        ) {
          const twoThirdsName = displayedAccounts.find(
            (a) => a.id === entries[0][0],
          )?.name;
          const oneThirdName = displayedAccounts.find(
            (a) => a.id === entries[1][0],
          )?.name;
          return (
            <>
              <span className="text-primary font-semibold">
                {twoThirdsName}
              </span>
              <span className="text-gray-600"> (67%) and </span>
              <span className="text-primary font-semibold">{oneThirdName}</span>
              <span className="text-gray-600"> (33%)</span>
            </>
          );
        }
        const accounts = entries.map(
          ([id]) => displayedAccounts.find((a) => a.id === id)?.name,
        );
        return (
          <>
            <span className="text-primary font-semibold">{accounts[0]}</span>
            <span className="text-gray-600"> (33%)</span>
            <span className="text-gray-600">, </span>
            <span className="text-primary font-semibold">{accounts[1]}</span>
            <span className="text-gray-600"> (33%)</span>
            <span className="text-gray-600">, and </span>
            <span className="text-primary font-semibold">{accounts[2]}</span>
            <span className="text-gray-600"> (34%)</span>
          </>
        );
      }

      case BillingSplitType.FIFTY_TWENTY_FIVE_TWENTY_FIVE: {
        const [majorityId, ...minorityIds] = Array.from(
          uniqueAccounts.entries(),
        )
          .sort(([, countA], [, countB]) => countB - countA)
          .map(([id]) => id);

        const majorityName = displayedAccounts.find(
          (a) => a.id === majorityId,
        )?.name;
        const minorityNames = minorityIds.map(
          (id) => displayedAccounts.find((a) => a.id === id)?.name,
        );
        return (
          <>
            <span className="text-primary font-semibold">{majorityName}</span>
            <span className="text-gray-600"> (50%), </span>
            <span className="text-primary font-semibold">
              {minorityNames[0]}
            </span>
            <span className="text-gray-600"> (25%), and </span>
            <span className="text-primary font-semibold">
              {minorityNames[1]}
            </span>
            <span className="text-gray-600"> (25%)</span>
          </>
        );
      }

      case BillingSplitType.TWENTY_FIVE_EACH: {
        const accounts = [1, 2, 3, 4].map((position) => {
          const account = currentPartnershipItem?.[`account${position}`];
          const id =
            (account as { id?: string } | null)?.id ??
            currentPartnershipItem?.[`account${position}Id`];
          return displayedAccounts.find((a) => a.id === id)?.name ?? id;
        });
        return (
          <>
            <span className="text-primary font-semibold">{accounts[0]}</span>
            <span className="text-gray-600"> (25%), </span>
            <span className="text-primary font-semibold">{accounts[1]}</span>
            <span className="text-gray-600"> (25%), </span>
            <span className="text-primary font-semibold">{accounts[2]}</span>
            <span className="text-gray-600"> (25%), and </span>
            <span className="text-primary font-semibold">{accounts[3]}</span>
            <span className="text-gray-600"> (25%)</span>
          </>
        );
      }

      case BillingSplitType.ONE_HUNDRED: {
        const accounts = Array.from(uniqueAccounts.entries()).map(([id]) => {
          return displayedAccounts.find((a) => a.id === id)?.name;
        });
        return (
          <>
            <span className="text-primary font-semibold">{accounts[0]}</span>
            <span className="text-gray-600"> (100%)</span>
          </>
        );
      }
      default:
        return 'No partnership defined';
    }
  };

  const isPartnershipCreated = currentPartnershipItem?.partnershipId !== null;
  const optionPositionsLine = getOptionPositionsLine();

  return (
    <div className={`bg-surface-card rounded-lg p-6 shadow-sm ${className}`}>
      <Tooltip target=".partnership-status-indicator" position="left" />
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm text-gray-600">{title}</span>
        <div
          className="partnership-status-indicator flex items-center"
          data-pr-tooltip={
            isPartnershipCreated
              ? 'Partnership created'
              : 'Partnership still needs to be created'
          }
        >
          {isPartnershipCreated ? (
            <i
              className="pi pi-check-circle text-lg text-green-500"
              role="img"
            />
          ) : (
            <i className="pi pi-times-circle text-lg text-red-500" role="img" />
          )}
        </div>
      </div>
      <p className="m-0 text-lg">{partnershipSummary()}</p>
      {optionPositionsLine && (
        <p className="mt-2 text-sm text-gray-700">{optionPositionsLine}</p>
      )}
      {getPartnerDistribution() && (
        <p className="mt-2 text-sm text-gray-500 italic">
          {getPartnerDistribution()}
        </p>
      )}
      {isInvalidDistribution() && (
        <p className="mt-2 flex items-center gap-1 text-sm font-medium text-red-500">
          <i className="pi pi-exclamation-triangle" />
          Ownership percentages do not add up to 100%
        </p>
      )}
    </div>
  );
};
